import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  AiInstructionUIFilter,
  ContentSnippetUIFilter,
  EmailPreviewState,
  IntentFilter,
  isSolveApiResponse,
  PreviewLogProps,
  PreviewLogType,
  SolveApiRequest,
  WorkflowPreviewSliceState,
} from './types';
import {
  getPreviewLogs,
  initializeConversation,
  updateConversation,
} from 'src/actions/api-preview/apiPreviewAction';
import { FormattedResponseItem } from 'src/pages/workflow-builder-edit/types';
import type { RootState } from 'src/store/rootReducer';
import { ContextVariableValue } from 'src/types/workflowBuilderAPITypes';

/**
 * Monotonic counter behind the client-side id stamped on every preview log.
 *
 * Preview logs carry no server identity, and setPreviewLanguage removes an
 * entry from the middle of the list before pushing a replacement -- which
 * shifts every index after it. Rendering by array index therefore reassigns
 * component state to the wrong log (visible on the stateful ones, e.g. the
 * image attachment summary). A stable id per log fixes that.
 *
 * Ephemeral and UI-only: never persisted, never sent back to the server.
 */
let previewLogIdCounter = 0;

const withPreviewLogIds = (logs: PreviewLogProps[]): PreviewLogProps[] =>
  logs.map(log => ({
    ...log,
    previewLogId: `log-${(previewLogIdCounter += 1)}`,
  }));

const initialState: WorkflowPreviewSliceState = {
  aiInstructionFilters: null,
  apiPreview: {
    conversation_id: null,
    history: [],
    isLoading: false,
    isStream: false,
  },
  channel: 'widget',
  contentSnippetsFilters: null,
  conversationId: null,
  emailPreview: {
    awaitingSideConversationReply: false,
    emailConversationId: null,
    error: null,
    isLoading: false,
    responseEmail: '',
    responseEmailGroups: [],
    sideConversationRecipients: {},
    stage: 'email-form',
    token: null,
  },
  executeTicketCreationInPreview: false,
  intentFilters: [],
  intentFiltersByChannel: {},
  language: 'en',
  previewLogs: [],
  subflowFilters: [],
  subflowFiltersByChannel: {},
  voicePreview: {
    conversationId: null,
    mode: 'call',
    outboundVersionSelection: null,
    outboundWorkflowId: null,
    previewPhoneNumber: null,
    webCallPreviewBrandId: null,
  },
  workflowCvs: [],
  workflowTag: null,
};

const workflowPreviewSlice = createSlice({
  extraReducers(builder) {
    // initialize conversation
    builder.addCase(initializeConversation.pending, state => {
      state.apiPreview.isLoading = true;
    });
    builder.addCase(initializeConversation.fulfilled, (state, { payload }) => {
      if (payload) {
        state.conversationId = payload.conversation_id;
        state.apiPreview.conversation_id = payload.conversation_id;
        state.apiPreview.history.push(payload);
      }

      state.apiPreview.isLoading = false;
    });
    builder.addCase(initializeConversation.rejected, (state, { payload }) => {
      if (payload) {
        state.apiPreview.history.push(payload);
      }

      state.apiPreview.isLoading = false;
    });

    // update conversation
    builder.addCase(updateConversation.pending, state => {
      state.apiPreview.isLoading = true;
    });
    builder.addCase(updateConversation.fulfilled, (state, { payload }) => {
      if (payload) {
        state.apiPreview.history.push(payload);
      }

      state.apiPreview.isLoading = false;
    });
    builder.addCase(updateConversation.rejected, (state, { payload }) => {
      if (payload) {
        state.apiPreview.history.push(payload);
      }

      state.apiPreview.isLoading = false;
    });
    builder.addCase(getPreviewLogs.fulfilled, (state, { payload }) => {
      state.previewLogs = state.previewLogs.concat(withPreviewLogIds(payload));
    });
  },
  initialState,
  name: 'workflowPreview',
  reducers: {
    addPreviewLogs(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<PreviewLogProps[]>,
    ) {
      state.previewLogs = state.previewLogs.concat(
        withPreviewLogIds(action.payload),
      );
    },
    clearPreviewLogs(state: WorkflowPreviewSliceState) {
      state.previewLogs = [];
    },
    initializeApiPreview(state: WorkflowPreviewSliceState) {
      state.apiPreview = {
        conversation_id: null,
        history: [],
        isLoading: false,
        isStream: state.apiPreview.isStream,
      };
      state.conversationId = null;
      state.previewLogs = [];
    },
    initializeEmailPreview(state: WorkflowPreviewSliceState) {
      state.emailPreview = {
        awaitingSideConversationReply: false,
        emailConversationId: null,
        error: null,
        isLoading: false,
        responseEmail: '',
        responseEmailGroups: [],
        sideConversationRecipients: {},
        stage: 'email-form',
        token: null,
      };
    },
    initializePreviewLogs(state: WorkflowPreviewSliceState) {
      state.previewLogs = withPreviewLogIds([
        {
          language_code: state.language,
          log_type: PreviewLogType.LANGUAGE_CHANGE,
        },
      ]);
      state.conversationId = null;
    },
    pushApiPreviewRequest(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<SolveApiRequest>,
    ) {
      // omit intent filters for preview and solve_api_token because they're internal only
      const request = action.payload;
      const requestToSave = {
        context_variables: request.context_variables,
        query: request.query,
        stream: request.stream,
      };
      state.apiPreview.history.push(requestToSave);
    },
    receiveApiPreviewChunk(state, action: PayloadAction<unknown>) {
      const { requestId, value } = action.payload as {
        requestId: number;
        value: unknown;
      };
      if (!isSolveApiResponse(value)) {
        return;
      }

      state.conversationId = value.conversation_id;
      state.apiPreview.conversation_id = value.conversation_id;
      state.apiPreview.history.push({ ...value, requestId });
    },
    restartApiPreviw(state: WorkflowPreviewSliceState) {
      state.apiPreview = {
        ...state.apiPreview,
        history: [],
      };
      state.previewLogs = [];
    },
    setAiInstructionFilters(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<AiInstructionUIFilter[] | null>,
    ) {
      state.aiInstructionFilters = action.payload;
    },
    setContentSnippetsFilters(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<ContentSnippetUIFilter[] | null>,
    ) {
      state.contentSnippetsFilters = action.payload;
    },
    setEmailPreviewAwaitingSideConversationReply(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.emailPreview.awaitingSideConversationReply = action.payload;
    },
    setEmailPreviewError(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<string | null>,
    ) {
      state.emailPreview.error = action.payload;
    },
    setEmailPreviewLoading(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.emailPreview.isLoading = action.payload;
    },
    setEmailPreviewResponseEmail(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<string>,
    ) {
      state.emailPreview.responseEmail = action.payload;
    },
    setEmailPreviewResponseEmailGroups(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<FormattedResponseItem[][]>,
    ) {
      state.emailPreview.responseEmailGroups = action.payload;
    },
    setEmailPreviewSideConversationRecipients(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<Record<string, string>>,
    ) {
      state.emailPreview.sideConversationRecipients = action.payload;
    },
    setEmailPreviewStage(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<WorkflowPreviewSliceState['emailPreview']['stage']>,
    ) {
      state.emailPreview.stage = action.payload;
    },
    setEmailPreviewState(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<EmailPreviewState>,
    ) {
      state.emailPreview = action.payload;
    },
    setEmailPreviewToken(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<string>,
    ) {
      state.emailPreview.token = action.payload;
    },
    setPreviewConversationId(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<string>,
    ) {
      state.conversationId = action.payload;
      state.apiPreview.conversation_id = action.payload;
      state.emailPreview.emailConversationId = action.payload;
    },
    setPreviewExecuteTicketCreation(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<boolean>,
    ) {
      state.executeTicketCreationInPreview = action.payload;
    },
    /**
     * Replaces the active intent filters. When `channel` is given, the
     * filters are also cached under that channel so the preview page can
     * restore the user's selections on a later visit; callers that only
     * borrow the slice (assist preview, AI instruction preview) omit it.
     */
    setPreviewIntentFilters(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<{ channel?: string; filters: IntentFilter[] }>,
    ) {
      state.intentFilters = action.payload.filters;
      if (action.payload.channel) {
        state.intentFiltersByChannel[action.payload.channel] =
          action.payload.filters;
      }
    },
    setPreviewIsStream(state, action: PayloadAction<boolean>) {
      state.apiPreview.isStream = action.payload;
    },
    setPreviewLanguage(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<string>,
    ) {
      state.language = action.payload;
      state.previewLogs = state.previewLogs.filter(
        log => log.log_type !== PreviewLogType.LANGUAGE_CHANGE,
      );
      // Removing an entry from the middle shifts every index after it, which
      // is why these carry an explicit id rather than being keyed by position.
      state.previewLogs.push(
        ...withPreviewLogIds([
          {
            language_code: action.payload,
            log_type: PreviewLogType.LANGUAGE_CHANGE,
          },
        ]),
      );
    },
    setPreviewSubflowFilters(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<{ channel?: string; filters: IntentFilter[] }>,
    ) {
      state.subflowFilters = action.payload.filters;
      if (action.payload.channel) {
        state.subflowFiltersByChannel[action.payload.channel] =
          action.payload.filters;
      }
    },
    setPreviewWorkflowCvs(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<{ name: string; value: ContextVariableValue }[]>,
    ) {
      state.workflowCvs = action.payload;
    },
    setPreviewWorkflowTag(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<string>,
    ) {
      state.workflowTag = action.payload;
    },
    setVoicePreviewConversationId(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<string>,
    ) {
      state.voicePreview.conversationId = action.payload;
    },
    setVoicePreviewMode(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<WorkflowPreviewSliceState['voicePreview']['mode']>,
    ) {
      state.voicePreview.mode = action.payload;
    },
    setVoicePreviewOutboundVersionSelection(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<
        WorkflowPreviewSliceState['voicePreview']['outboundVersionSelection']
      >,
    ) {
      if (
        action.payload === null ||
        action.payload.intentWorkflowId ===
          state.voicePreview.outboundWorkflowId
      ) {
        state.voicePreview.outboundVersionSelection = action.payload;
      }
    },
    setVoicePreviewOutboundWorkflowId(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<string>,
    ) {
      if (state.voicePreview.outboundWorkflowId === action.payload) {
        return;
      }
      state.voicePreview.outboundWorkflowId = action.payload;
      // Clear version selection when workflow changes
      state.voicePreview.outboundVersionSelection = null;
    },
    setVoicePreviewPhoneNumber(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<string>,
    ) {
      state.voicePreview.previewPhoneNumber = action.payload;
    },
    setWebCallPreviewBrandId(
      state: WorkflowPreviewSliceState,
      action: PayloadAction<string>,
    ) {
      state.voicePreview.webCallPreviewBrandId = action.payload;
    },
  },
});

export const selectPreviewLanguage = (
  state: RootState,
): WorkflowPreviewSliceState['language'] => {
  return state.workflowPreview.language;
};

export const selectPreviewWorkflowTag = (
  state: RootState,
): WorkflowPreviewSliceState['workflowTag'] => {
  return state.workflowPreview.workflowTag;
};

export const selectPreviewIntentFilters = (
  state: RootState,
): WorkflowPreviewSliceState['intentFilters'] => {
  return state.workflowPreview.intentFilters;
};

export const selectPreviewExecuteTicketCreation = (
  state: RootState,
): WorkflowPreviewSliceState['executeTicketCreationInPreview'] => {
  return state.workflowPreview.executeTicketCreationInPreview;
};

export const selectPreviewSubflowFilters = (
  state: RootState,
): WorkflowPreviewSliceState['subflowFilters'] => {
  return state.workflowPreview.subflowFilters;
};

export const selectPreviewIntentFiltersForChannel = (
  state: RootState,
  channel: string,
): IntentFilter[] | undefined => {
  return state.workflowPreview.intentFiltersByChannel[channel];
};

export const selectPreviewSubflowFiltersForChannel = (
  state: RootState,
  channel: string,
): IntentFilter[] | undefined => {
  return state.workflowPreview.subflowFiltersByChannel[channel];
};

/**
 * Combined selector for all filters (intents + subflows).
 * Use this when sending filters to the backend.
 */
export const selectAllPreviewFilters = createSelector(
  [selectPreviewIntentFilters, selectPreviewSubflowFilters],
  (intentFilters, subflowFilters): IntentFilter[] => [
    ...intentFilters,
    ...subflowFilters,
  ],
);

export const selectVoicePreviewOutboundWorkflowId = (
  state: RootState,
): WorkflowPreviewSliceState['voicePreview']['outboundWorkflowId'] => {
  return state.workflowPreview.voicePreview.outboundWorkflowId;
};

export const selectVoicePreviewOutboundVersionSelection = (
  state: RootState,
): WorkflowPreviewSliceState['voicePreview']['outboundVersionSelection'] => {
  return state.workflowPreview.voicePreview.outboundVersionSelection;
};

export const selectVoicePreviewConversationId = (
  state: RootState,
): WorkflowPreviewSliceState['voicePreview']['conversationId'] => {
  return state.workflowPreview.voicePreview.conversationId;
};

export const selectAiInstructionFilters = (
  state: RootState,
): AiInstructionUIFilter[] | null => state.workflowPreview.aiInstructionFilters;

export const selectContentSnippetFilters = (
  state: RootState,
): ContentSnippetUIFilter[] | null =>
  state.workflowPreview.contentSnippetsFilters;

export const selectPreviewConversationId = (
  state: RootState,
): WorkflowPreviewSliceState['conversationId'] => {
  return state.workflowPreview.conversationId;
};

export const selectPreviewWorkflowCvs = (
  state: RootState,
): WorkflowPreviewSliceState['workflowCvs'] => {
  return state.workflowPreview.workflowCvs;
};

export const selectPreviewLogs = (
  state: RootState,
): WorkflowPreviewSliceState['previewLogs'] => {
  return state.workflowPreview.previewLogs;
};

export const selectApiPreviewState = (
  state: RootState,
): WorkflowPreviewSliceState['apiPreview'] => {
  return state.workflowPreview.apiPreview;
};

export const selectEmailPreviewStage = (
  state: RootState,
): WorkflowPreviewSliceState['emailPreview']['stage'] => {
  return state.workflowPreview.emailPreview.stage;
};

export const selectEmailPreviewResponseEmail = (
  state: RootState,
): WorkflowPreviewSliceState['emailPreview']['responseEmail'] => {
  return state.workflowPreview.emailPreview.responseEmail;
};

export const selectEmailPreviewConversationId = (
  state: RootState,
): WorkflowPreviewSliceState['emailPreview']['emailConversationId'] => {
  return state.workflowPreview.emailPreview.emailConversationId;
};

export const selectEmailPreviewToken = (
  state: RootState,
): WorkflowPreviewSliceState['emailPreview']['token'] => {
  return state.workflowPreview.emailPreview.token;
};

export const selectEmailPreviewError = (state: RootState): string | null => {
  return state.workflowPreview.emailPreview.error;
};

export const selectEmailPreviewResponseEmailGroups = (
  state: RootState,
): FormattedResponseItem[][] => {
  return state.workflowPreview.emailPreview.responseEmailGroups;
};

export const selectEmailPreviewAwaitingSideConversationReply = (
  state: RootState,
): boolean => {
  return state.workflowPreview.emailPreview.awaitingSideConversationReply;
};

export const selectEmailPreviewSideConversationRecipients = (
  state: RootState,
): Record<string, string> => {
  return state.workflowPreview.emailPreview.sideConversationRecipients;
};

export const selectEmailPreviewLoading = (state: RootState): boolean => {
  return state.workflowPreview.emailPreview.isLoading;
};

export const selectIntentNameForIntentId =
  (intentId: string | null) =>
  (state: RootState): string => {
    if (!intentId) {
      return '';
    }
    const foundFilter = state.workflowPreview.intentFilters.find(
      filter => filter.intentId === intentId,
    );

    return foundFilter?.intentName || '';
  };

export const selectPreviewPhoneNumber = (
  state: RootState,
): WorkflowPreviewSliceState['voicePreview']['previewPhoneNumber'] => {
  return state.workflowPreview.voicePreview.previewPhoneNumber;
};

export const selectWebCallPreviewBrandId = (
  state: RootState,
): WorkflowPreviewSliceState['voicePreview']['webCallPreviewBrandId'] => {
  return state.workflowPreview.voicePreview.webCallPreviewBrandId;
};

export const selectVoicePreviewMode = (
  state: RootState,
): WorkflowPreviewSliceState['voicePreview']['mode'] => {
  return state.workflowPreview.voicePreview.mode;
};

export default workflowPreviewSlice.reducer;

export const {
  addPreviewLogs,
  clearPreviewLogs,
  initializeApiPreview,
  initializeEmailPreview,
  initializePreviewLogs,
  pushApiPreviewRequest,
  receiveApiPreviewChunk,
  restartApiPreviw,
  setAiInstructionFilters,
  setContentSnippetsFilters,
  setEmailPreviewAwaitingSideConversationReply,
  setEmailPreviewError,
  setEmailPreviewLoading,
  setEmailPreviewResponseEmail,
  setEmailPreviewResponseEmailGroups,
  setEmailPreviewSideConversationRecipients,
  setEmailPreviewStage,
  setEmailPreviewState,
  setEmailPreviewToken,
  setPreviewConversationId,
  setPreviewExecuteTicketCreation,
  setPreviewIntentFilters,
  setPreviewIsStream,
  setPreviewLanguage,
  setPreviewSubflowFilters,
  setPreviewWorkflowCvs,
  setPreviewWorkflowTag,
  setVoicePreviewConversationId,
  setVoicePreviewMode,
  setVoicePreviewOutboundVersionSelection,
  setVoicePreviewOutboundWorkflowId,
  setVoicePreviewPhoneNumber,
  setWebCallPreviewBrandId,
} = workflowPreviewSlice.actions;
